import { Modal } from '../../Modal';
import { IProjectPosition, IProjectPositionStringSkills } from '../../../../interfaces/projectPosition';
import { IProjectApplicant } from '../../../../interfaces/projectApplicant';
import { IProjectApplicantRequest } from '../../../../interfaces/projectApplicantRequest';
import { TEXTS } from '../../../../core/texts';
import { PersonAdd } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { FlexBox, FlexBoxFullWidth } from '../../boxes';
import { Box, Button } from '@mui/material';
import { ButtonWithLoader } from '../../ButtonWithLoader';
import ApplicantFreelancersTable from './ApplicantFreelancersTable';
import { useSearchParams } from 'react-router-dom';
import { ControlledInputOnBlur } from '../../ControlledInputOnBlur';
import { GET_PROJECT_POSITIONS_QUERY_KEY } from '../../../../queryHooks/project/useGetProjectPositions';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../interfaces/errorResponse';
import { parseAxiosError } from '../../../../services/axios';
import { useNotification } from '../../../../+state/useNotification';

interface IProps {
  positionEntity: IProjectPosition | IProjectPositionStringSkills;
  handleAddApplicant: (applicant: IProjectApplicantRequest) => Promise<IProjectApplicant>;
};

const AddApplicantModal = ({ handleAddApplicant, positionEntity }: IProps) => {
  const [freelancerId, setFreelancerId] = useState<null | number>(null);
  const [message, setMessage] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { openSuccessToast, openErrorToast } = useNotification();

  const initialParams = useMemo(() => {
    const page = searchParams.get('page');
    const size = searchParams.get('size');
    return page && size ? { page, size } : [];
  }, [searchParams]);

  const handleCloseModal = useCallback(() => {
    setSearchParams(initialParams);
  }, [setSearchParams, initialParams]);

  const mutation = useMutation({
    mutationFn: (applicant: IProjectApplicantRequest) => handleAddApplicant(applicant),
    onSuccess: (data) => {
      queryClient.invalidateQueries([GET_PROJECT_POSITIONS_QUERY_KEY]);
      openSuccessToast(TEXTS.SUCCESS_ADDED);
      handleCloseModal();
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const handleMessageChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  }, []);

  const handleAddBtn = useCallback(() => {
    const applicant: Omit<IProjectApplicantRequest, 'freelancerId' | 'message'> = {
      projectOpportunityId: positionEntity.projectOpportunityId,
      positionId: positionEntity.id,
      rate: positionEntity.preferredRate,
    };
    if (!freelancerId) {
      return openErrorToast(TEXTS.INVALID_DATA);
    }
    mutation.mutate({ ...applicant, freelancerId, message });
  }, [mutation, freelancerId, positionEntity, message, openErrorToast]);


  return (
    <Modal onClose={handleCloseModal} keepMounted={true} title={TEXTS.ADD_NEW_APPLICANT}
           headerIcon={<PersonAdd color={'primary'}/>}
           dialogActions={<FlexBoxFullWidth alignItems={'center'} justifyContent={'space-between'} p={1} mt={3}
           >
             <Box>
               <Button onClick={handleCloseModal} variant={'outlined'} color={'error'}>{TEXTS.CANCEL}</Button>
             </Box>
             <FlexBox alignItems={'center'}>
               {
                 freelancerId &&
                 <ControlledInputOnBlur sx={{ width: { mobile: '100%', tablet: 300 }, mr: 1 }} label={TEXTS.MESSAGE}
                                        multiline onChange={handleMessageChange} value={message}
                 />
               }
               <ButtonWithLoader loading={mutation.isLoading} onClick={handleAddBtn} variant={'outlined'}
                                 disabled={!freelancerId}>
                 {TEXTS.ADD_APPLICANT}
               </ButtonWithLoader>
             </FlexBox>
           </FlexBoxFullWidth>}
    >
      <ApplicantFreelancersTable
        projectPosition={positionEntity}
        selectedFreelancerId={freelancerId}
        setSelectedFreelancerId={setFreelancerId}
      />
    </Modal>
  );
};

export default AddApplicantModal;